import { useFetchData } from "../../../hooks/useFetch";
import { API_SERVICE_TYPE_MAPS_ROUTER, ApiConfig,API_SERVICE_ROUTER } from "../../../services/api-config";

export enum ServiceMapStatusCode {
  INACTIVE = 0,
  ACTIVE = 1,
  DELETED = 2,
}

/**
 * Bản chất của một dòng bán được.
 *
 * Chỉ hai giá trị: kiểm đếm chạy trên loại đồ, không chạy trên dòng đơn, nên không cần phân loại
 * "cái gì đếm được" ở đây. Việc duy nhất `kind` gánh là tách phụ phí — thứ tính theo *lần* và
 * không tiêu hao hoá chất theo khối lượng.
 */
export enum SERVICE_MAP_KIND {
  SERVICE = 1,
  SURCHARGE = 2,
}

export const SERVICE_MAP_KIND_NAME: Record<number, string> = {
  [SERVICE_MAP_KIND.SERVICE]: "Dịch vụ",
  [SERVICE_MAP_KIND.SURCHARGE]: "Phụ phí",
};

export const SERVICE_MAP_KIND_HINT: Record<number, string> = {
  [SERVICE_MAP_KIND.SERVICE]: "Giặt, sấy, ủi… tính theo kg hoặc theo cái",
  [SERVICE_MAP_KIND.SURCHARGE]: "Phát sinh theo lần, VD: Giặt Express. Không tính hoá chất theo cân",
};

export interface ServiceMap {
  id: number;
  branch_id: number;
  service_id: number;
  service_type_id: number;
  price?: number | string;
  status: ServiceMapStatusCode | number;
  created_at?: string;
  updated_at?: string;

  // --- Tính tiền theo dòng (product/docs/vat-tu-va-tinh-tien-ky-thuat.md) ---
  /** Đơn vị bán: kg · cái · lần. */
  unit_id?: number;
  unit_name?: string;
  /** Khối lượng quy đổi của 1 đơn vị bán, tính bằng kg. Dòng bán theo kg thì bằng 1. */
  weight_per_unit?: number;
  kind?: number;
  /** CSV id loại đồ liên kết, ví dụ "1,2,3". */
  linked_category_ids?: string | null;
}

export type ServiceMapListResponse = ServiceMap[];

export interface ServiceMapListFilters {
  status?: number;
  branch_id?: number;
  /** Lọc theo nhóm dịch vụ (services.id). */
  service_id?: number;
  limit?: number;
  page?: number;
  /** Tìm theo tên nhóm, tên hoặc mã dịch vụ. */
  key_search?: string;
}

/** Các trường sửa được của 1 dòng bảng giá. Nhóm / dịch vụ không đổi được sau khi tạo. */
export interface UpdateMappingPayload {
  price?: number;
  unit_id?: number;
  weight_per_unit?: number;
  kind?: number;
  /** CSV id loại đồ liên kết. */
  linked_category_ids?: string;
  status?: ServiceMapStatusCode;
  description?: string;
}

/** Tạo 1 dòng bảng giá: gắn 1 dịch vụ vào 1 nhóm. */
export interface CreateMappingPayload extends UpdateMappingPayload {
  service_id: number;
  service_type_id: number;
  price: number;
}

export interface CreateServiceMapPayload {
  branch_id: number;
  service_id: number;
  service_type_id: number;
  price?: number;
  status?: ServiceMapStatusCode;
}

export interface UpdateServiceMapPayload {
  price?: number;
  status?: ServiceMapStatusCode;
}
export interface ServiceTypePricePatch {
  service_type_id: number;
  price: number;
  status?: ServiceMapStatusCode;

  // Chỉ gửi khi người dùng thực sự đổi: backend giữ nguyên giá trị cũ với khoá vắng mặt, nên lần
  // sửa chỉ đổi giá sẽ không vô tình xoá đơn vị bán hay loại đồ liên kết đã khai.
  unit_id?: number;
  weight_per_unit?: number;
  kind?: number;
  linked_category_ids?: string;
}
export interface UpdateServiceMappingPayload {
  service_name?: string;
  service_code?: string; 
  image_url?: string;
  serviceTypePrices?: ServiceTypePricePatch[];
}
export interface UpdateServiceMappingResponse {
  service_id: number;
  service_name: string;
  service_code: string;
  image_url: string;
  updatedTypePrices: Array<{
    service_type_id: number;
    price: number;
  }>;
}
export interface ChangeServiceMapStatusPayload {
  status: ServiceMapStatusCode;
}

export const ServiceMapService = {
  list: () => {
    const { request, baseResponse, loading } =
      useFetchData.Get<ServiceMapListResponse>({
        projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
      });
    return {
      fetch: (params: ServiceMapListFilters = { status: -1 }) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading: loading,
    }

  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: CreateServiceMapPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  /** Tạo 1 dòng bảng giá. */
  createMapping: () => {
    const { request, loading } = useFetchData.Post<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (payload: CreateMappingPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_CREATE_MAPPING,
          data: payload,
        }),
      loading,
    };
  },

  /** Sửa 1 dòng bảng giá. */
  updateMapping: () => {
    const { request, loading } = useFetchData.Post<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateMappingPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_UPDATE_MAPPING(id),
          data: payload,
        }),
      loading,
    };
  },

  updatePrice: () => {
    const { request, baseResponse, loading } = useFetchData.Post<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      updatePrice: (id: number, payload: UpdateServiceMapPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_CHANGE_PRICE_SERVICE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    }
  },
  update : () => {
    const { request, baseResponse, loading } = useFetchData.Post<UpdateServiceMappingResponse>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (serviceId: number, payload: UpdateServiceMappingPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_UPDATE_SERVICE(serviceId),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },
  changeStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Post<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      changeStatus: (id: number, payload: ChangeServiceMapStatusPayload) =>
        request({
          endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_CHANGE_STATUS_SERVICE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    }
  },

  detail: () => {
    const { request, baseResponse, loading } = useFetchData.Get<ServiceMap>({
      projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_SERVICE_ROUTER.API_DETAIL_SERVICE(id),
        }),
      response: baseResponse,
      loading: loading,
    }
  },
  listByType: () => {
    const { request, baseResponse, loading } =
      useFetchData.Get<ServiceMapListResponse>({
        projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE,
      });
      return {
        fetch: (params: ServiceMapListFilters = { status: -1 }) =>
          request({
            endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_LIST,
            data: params,
          }),
        response: baseResponse,
        loading: loading,
      }
  },
};

export default ServiceMapService;
